export interface PosthogRuntimeConfig {
  apiHost: string;
  token: string;
}

export interface PosthogClient {
  identify: (distinctId: string, properties?: Record<string, unknown>) => unknown;
  init: (token: string, options: Record<string, unknown>) => unknown;
  register: (properties: Record<string, unknown>) => unknown;
  register_for_session: (properties: Record<string, unknown>) => unknown;
  reset: () => unknown;
}

type ErrorHandler = (error: unknown) => void;

const getAnonymousDistinctId = (anonymousUserId: string) => `anonymous:${anonymousUserId}`;

export class PosthogController {
  private sdkInitialized = false;
  private currentIdentity: string | null = null;

  constructor(
    private readonly client: PosthogClient,
    private readonly onError: ErrorHandler = error => console.error('[PostHog]', error),
    private readonly additionalInitOptions: Record<string, unknown> = {},
  ) {}

  initializeAnonymousUser(
    anonymousUserId: string | null | undefined,
    config: PosthogRuntimeConfig,
  ): void {
    if (!anonymousUserId || !this.hasConfig(config)) {
      return;
    }

    const distinctId = getAnonymousDistinctId(anonymousUserId);
    if (this.currentIdentity === distinctId) {
      return;
    }

    const wasInitialized = this.sdkInitialized;
    if (!this.initializeSdk(config, {
      distinctID: distinctId,
      isIdentifiedID: false,
    })) {
      return;
    }

    try {
      if (wasInitialized) {
        this.client.register({ distinct_id: distinctId });
      }
      this.client.register_for_session({
        wegic_anonymous_user_id: anonymousUserId,
        wegic_identity_type: 'anonymous',
      });
      this.currentIdentity = distinctId;
    } catch (error) {
      this.onError(error);
    }
  }

  identifyAuthenticatedUser(
    userId: string | null | undefined,
    userProperties: Record<string, unknown>,
    config: PosthogRuntimeConfig,
  ): void {
    if (!userId || !this.hasConfig(config)) {
      return;
    }

    const identity = `authenticated:${userId}`;
    if (this.currentIdentity === identity || !this.initializeSdk(config)) {
      return;
    }

    try {
      this.client.register_for_session({ wegic_identity_type: 'authenticated' });
      this.client.identify(userId, userProperties);
      this.currentIdentity = identity;
    } catch (error) {
      this.onError(error);
    }
  }

  reset(): void {
    if (!this.sdkInitialized) {
      return;
    }

    try {
      this.client.reset();
      this.currentIdentity = null;
    } catch (error) {
      this.onError(error);
    }
  }

  private initializeSdk(
    config: PosthogRuntimeConfig,
    bootstrap?: { distinctID: string; isIdentifiedID: false },
  ): boolean {
    if (this.sdkInitialized) {
      return true;
    }

    try {
      this.client.init(config.token, {
        api_host: config.apiHost,
        person_profiles: 'identified_only',
        capture_pageview: false,
        capture_pageleave: true,
        session_recording: {
          recordCrossOriginIframes: true,
        },
        ...this.additionalInitOptions,
        ...(bootstrap ? { bootstrap } : {}),
      });
      this.sdkInitialized = true;
      return true;
    } catch (error) {
      this.onError(error);
      return false;
    }
  }

  private hasConfig(config: PosthogRuntimeConfig): boolean {
    return Boolean(config.token && config.apiHost);
  }
}
